import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Check, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import useProjectStore from "@/shared/zustand/projectManagementStore";
import useToastStore from "@/shared/zustand/toastStore";
import { EditFeedback } from "./EditFeedback";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { Skeleton } from "primereact/skeleton";
import { useMainLayoutStore } from "@/shared/zustand/mainLayoutStore";
import { modesToAssisstantName } from "@/constants";
import { updateFeedbackUpsert } from "@/core/api/api";
const HighlightedText = ({ text }) => {
    if (!text) {
        return null;
    }
    // Split the text by angle brackets and map through the parts
    const parts = text.split(/(<[^>]+>)/);
    return (_jsx("span", { children: parts.map((part, index) => {
            if (part.startsWith("<") && part.endsWith(">")) {
                const innerText = part.slice(1, -1);
                return (_jsxs("span", { className: " text-red-500 ", children: ["<", innerText, ">"] }, index));
            }
            return _jsx("span", { children: part }, index);
        }) }));
};
export const FeedbackContent = ({ onHide, feedbackData, fromInstantLearning, }) => {
    console.log("Feedback data is ", feedbackData);
    const { activeMode, modesHistory } = useMainLayoutStore();
    const lastMode = useMemo(() => {
        return modesHistory.length > 0 && modesHistory[modesHistory.length - 1]?.mode
            ? modesHistory[modesHistory.length - 1].mode
            : "";
    }, [modesHistory]);
    // Initialize formData with activeMode
    // const defaultFormState: FeedbackForm = useMemo(
    //   () => ({
    //     originalContent: "",
    //     suggestedReplacement: "",
    //     applyTo: activeMode || "Mode Level", // Use activeMode if available, fallback to "Mode Level"
    //     whenToApply: "",
    //     whenNotToApply: "",
    //   }),
    //   [activeMode]
    // );
    const defaultFormState = useMemo(() => ({
        originalContent: "",
        suggestedReplacement: "",
        applyTo: lastMode || "Mode Level", // Use lastMode if available, fallback to "Mode Level"
        whenToApply: "",
        whenNotToApply: "",
    }), [lastMode]);
    const [formData, setFormData] = useState(defaultFormState);
    const [isApplyClicked, setIsApplyClicked] = useState(false);
    // console.log("isAppliyClicked: ", isApplyClicked);
    // console.log("Form data in FeedbackContent: ", formData);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [feedbackList, setFeedbackList] = useState([]);
    // const [applyToOptions, setApplyToOptions] = useState<
    //   { label: string; value: string }[]
    // >([
    //   activeMode
    //     ? {
    //         label: activeMode,
    //         value: activeMode,
    //       }
    //     : { label: "Mode Level", value: "Mode Level" },
    // ]);
    const [applyToOptions, setApplyToOptions] = useState([
        lastMode
            ? {
                label: lastMode,
                value: lastMode,
            }
            : { label: "Mode Level", value: "Mode Level" },
    ]);
    // console.log("applyToOptions: ", applyToOptions);
    const [searchParams, setSearchParams] = useSearchParams();
    const { selectedProject } = useProjectStore();
    const exisitingProjectId = searchParams.get("projectId");
    const projectId = exisitingProjectId || selectedProject?._id;
    const showToast = useToastStore((state) => state.showToast);
    const [smartSuggestions, setSmartSuggestions] = useState([]);
    const { search, isLoading, results, error, streamData } = useDebouncedSearch({
        shallCallGraphqlCall: !isApplyClicked, // Only call GraphQL if it's not from Apply button
        delay: 1000,
    });
    useMemo(() => {
        if (feedbackData) {
            const newApplyToOptions = feedbackData.modeName
                ? [{ label: feedbackData.modeName, value: feedbackData.modeName }]
                : [];
            setApplyToOptions((prev) => {
                // Check if the option already exists to avoid duplicates
                const exists = prev.some((option) => option.value === feedbackData.modeName);
                return exists ? prev : [...prev, ...newApplyToOptions];
            });
            setFormData((prev) => ({
                ...prev,
                originalContent: feedbackData.feedbackData.originalContent || "",
                suggestedReplacement: feedbackData.feedbackData.suggestedReplacement || "",
                // applyTo: feedbackData.feedbackData.applyTo || "Mode Level",
                // applyTo: feedbackData.modeName || activeMode,
                applyTo: feedbackData.modeName || lastMode,
                whenToApply: feedbackData.feedbackData.whenToApply || "",
                whenNotToApply: feedbackData.feedbackData.whenNotToApply || "",
            }));
            // Trigger debounced search with the originalContent
            // const instantAcitveMode = useMainLayoutStore.getState().activeMode;
            const currentLastMode = useMainLayoutStore.getState().modesHistory.length > 0 && modesHistory[modesHistory.length - 1]?.mode
                ? modesHistory[modesHistory.length - 1].mode
                : "";
            // const assistantName = modesToAssisstantName[instantAcitveMode];
            const assistantName = modesToAssisstantName[currentLastMode];
            // console.log("printing check assistantName", assistantName);
            const finalAssistantName = assistantName || feedbackData.modeAssistantName;
            // console.log("printing check finalAssistantName", finalAssistantName);
            if (feedbackData.feedbackData.originalContent && finalAssistantName) {
                search(feedbackData.feedbackData.originalContent, finalAssistantName);
            }
        }
    }, [feedbackData]);
    // console.log("results from api: ", results);
    // console.log("stream data in feedback: ", streamData);
    useMemo(() => {
        if (streamData.length > 0) {
            const listOfLearnings = streamData.flat();
            setSmartSuggestions((prevData) => {
                const uniqueData = [...prevData];
                listOfLearnings.forEach((item) => {
                    if (!uniqueData.includes(item)) {
                        uniqueData.push(item);
                    }
                });
                return uniqueData;
            });
        }
    }, [streamData]);
    useMemo(() => {
        if (results.length > 0) {
            const mappedFeedback = results.map((result) => ({
                id: result.id,
                originalContent: result.originalContent,
                suggestedReplacement: result.suggestedReplacement,
                applyTo: result.applyTo,
                whenToApply: result.whenToApply,
                whenNotToApply: result.whenNotToApply,
            }));
            setFeedbackList(mappedFeedback);
        }
    }, [results]);
    // Update formData when lastMode changes
    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            applyTo: lastMode || prev.applyTo,
        }));
        setApplyToOptions((prev) => {
            // Check if the option already exists
            const exists = prev.some((option) => option.value === lastMode);
            if (!exists && lastMode) {
                return [...prev, { label: lastMode, value: lastMode }];
            }
            return prev;
        });
    }, [lastMode]);
    const handleInputChange = (field, value, fromApply = false) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
        // If the field being changed is originalContent, trigger the debounced search
        if (field === "originalContent") {
            if (!fromApply) {
                setIsApplyClicked(false); // Reset the flag only for direct user input
            }
            search(value, feedbackData.modeAssistantName);
        }
    };
    const handleEdit = (feedback) => {
        setSelectedFeedback(feedback);
        setIsEditing(true);
    };
    const handleUpdateFeedback = async (updatedData) => {
        try {
            const updatedList = feedbackList.map((item) => item.id === updatedData.id ? updatedData : item);
            // if(!projectId){
            //   console.error("No project ID provided");
            //   showToast("error", "Error", "Select a project first");
            //   return;
            // }
            // const tableName = `cw-${projectId}-guide`;
            if (!feedbackData.modeAssistantName) {
                console.error("No mode assistant name provided");
                showToast("error", "Error", "Provide the mode assistant name ");
                return;
            }
            const tableName = `${feedbackData.modeAssistantName}-agentGuide`;
            setFeedbackList(updatedList);
            // console.log("Updated feedback:", updatedData);
            // const upsertFeedbackPaylaod = {
            //   query: updatedData.originalContent || "",
            //   metadata: {
            //     text: updatedData.originalContent || "",
            //   },
            //   variantsToAdd: [
            //     {
            //       variant: updatedData.suggestedReplacement || "",
            //       conditions_whentoapply: updatedData.whenToApply || "",
            //       conditions_whennottoapply: updatedData.whenNotToApply || "",
            //     },
            //   ],
            //   feedbackToAdd: updatedData.suggestedReplacement || "",
            //   numberOfVariants: 1,
            //   tableName: tableName,
            // };
            const upsertFeedbackPayload = {
                inputQuestion: updatedData.originalContent || "", // inputQuestion
                feedback: updatedData.suggestedReplacement || "", // feedback
                whenToApply: updatedData.whenToApply || "", // whenToApply
                whenNotToApply: updatedData.whenNotToApply || "", //whenNotToApply
                assistantName: feedbackData.modeAssistantName, // assistantName
                scope: "Assistant", //scope - > Assistant | Globa
            };
            // old code for api call
            // const responseFromUpsert = await axios.post(
            //   `${process.env.VITE_BASE_API_URL}/feedback/upsert_feedback`,
            //   upsertFeedbackPayload
            // );
            const { responseData, error } = await updateFeedbackUpsert(upsertFeedbackPayload);
            if (error) {
                console.error("Error updating feedback:", error);
                showToast("error", "Error", "Error updating feedback");
                return;
            }
            // console.log("Feedback upserted:", responseData?.data);
            showToast("success", "Success", "Feedback saved successfully");
            setIsEditing(false);
            setSelectedFeedback(null);
            onHide();
        }
        catch (error) {
            console.error("Error updating feedback:", error);
            showToast("error", "Error", "Error updating feedback");
        }
    };
    const handleSave = async () => {
        try {
            // console.log("New feedback:", formData);
            // if (!projectId) {
            //   console.error("No project ID provided");
            //   showToast("error", "Error", "Select a project first");
            //   return;
            // }
            // const tableName = `cw-${projectId}-guide`;
            if (!feedbackData.modeAssistantName) {
                console.error("No mode assistant name provided");
                showToast("error", "Error", "Provide the mode assistant name ");
                return;
            }
            const tableName = `${feedbackData.modeAssistantName}-agentGuide`;
            // const response = await axios.post(
            //   `${process.env.VITE_BASE_API_URL}/feedback/create_feedback_table`,
            //   {
            //     table_name: tableName,
            //   }
            // );
            // console.log("Feedback table created:", response.data);
            // const upsertFeedbackPaylaod = {
            //   query: formData.originalContent || "",
            //   metadata: {
            //     text: formData.originalContent || "",
            //   },
            //   variantsToAdd: [
            //     {
            //       variant: formData.suggestedReplacement || "",
            //       conditions_whentoapply: formData.whenToApply || "",
            //       conditions_whennottoapply: formData.whenNotToApply || "",
            //     },
            //   ],
            //   feedbackToAdd: formData.suggestedReplacement || "",
            //   numberOfVariants: 1,
            //   tableName: tableName,
            // };
            const upsertFeedbackPaylaod = {
                inputQuestion: formData.originalContent || "", // inputQuestion
                feedback: formData.suggestedReplacement || "", // feedback
                whenToApply: formData.whenToApply || "", // whenToApply
                whenNotToApply: formData.whenNotToApply || "", //whenNotToApply
                assistantName: feedbackData.modeAssistantName, // assistantName
                scope: "Assistant", //scope - > Assistant | Globa
            };
            // old code of api call
            // const responseFromUpsert = await axios.post(
            //   `${process.env.VITE_BASE_API_URL}/feedback/upsert_feedback`,
            //   upsertFeedbackPaylaod
            // );
            const { responseData, error } = await updateFeedbackUpsert(upsertFeedbackPaylaod);
            if (error) {
                console.error("Error updating feedback:", error);
                showToast("error", "Error", "Error updating feedback");
                return;
            }
            // console.log("Feedback upserted:", responseData?.data);
            showToast("success", "Success", "Feedback saved successfully");
            onHide();
        }
        catch (error) {
            console.error("Error saving feedback:", error);
        }
    };
    const handleSuggestion = (suggestion) => {
        setIsApplyClicked(true); // Set flag when Apply is clicked
        handleInputChange("originalContent", suggestion, true); // Pass true to indicate this is from Apply button
    };
    const renderCreateForm = () => (_jsxs("div", { className: "font-content text-base flex flex-col gap-y-4 h-full overflow-y-auto", children: [_jsxs("div", { className: "", children: [_jsx("h3", { className: "mb-2 font-medium", children: "Question" }), _jsx(InputText, { className: "w-full p-3 text-sm rounded-[6px] border border[#d1d5db]", value: formData.originalContent, onChange: (e) => handleInputChange("originalContent", e.target.value), placeholder: "Original content, for which variant is being added" })] }), _jsxs("div", { className: "", children: [_jsx("h3", { className: "mb-2 font-medium", children: "How to Respond" }), _jsx(InputText, { className: "w-full p-3 text-sm rounded-[6px] border border[#d1d5db]", value: formData.suggestedReplacement, onChange: (e) => handleInputChange("suggestedReplacement", e.target.value), placeholder: "Enter your variant here" })] }), _jsxs("div", { className: "gap-y-4 flex flex-col", children: [isLoading && (_jsxs("div", { className: "w-full h-[50px] bg-[#fff] border border-[#d1d5db] rounded-[6px] p-4 flex flex-row justify-between items-center", children: [_jsx(Skeleton, { width: "30%", height: "14px" }), _jsx(Skeleton, { width: "14px", height: "14px", className: "mr-2" })] })), smartSuggestions.length > 0 && (_jsx(Accordion, { className: "border border-[#d1d5db] shadow-none rounded-[6px]", children: _jsx(AccordionTab, { header: "Smart Replacement Suggestions", className: "shadow-none rounded-[6px] m-0", headerClassName: "text-sm shadow-none rounded-[6px]", contentClassName: "rounded-b-[6px]", children: _jsx("div", { className: "space-y-2", children: smartSuggestions.length === 0 ? (_jsx("div", { className: "flex justify-between items-center p-2 bg-gray-50 rounded text-sm", children: "No Smart Replacement Suggestions found" })) : (smartSuggestions.map((suggestion) => (_jsxs("div", { className: "flex flex-wrap gap-2 items-start p-2 bg-gray-50 rounded text-sm", children: [_jsx("div", { className: "flex-1 min-w-1/2 break-words", children: _jsx(HighlightedText, { text: suggestion }) }), _jsx(Button, { label: "Apply", className: "bg-blue-50 text-blue-500 rounded-[6px] flex items-center border border-[#D1D5DB] shrink-0", onClick: () => handleSuggestion(suggestion) })] }, suggestion)))) }) }) })), feedbackList.length > 0 && (_jsx(Accordion, { className: "border border-[#d1d5db] shadow-none rounded-[6px]", children: _jsx(AccordionTab, { header: "Similar Learnings already exist!", className: "shadow-none rounded-[6px] m-0", headerClassName: "text-sm shadow-none rounded-[6px]", contentClassName: "rounded-b-[6px]", children: _jsx("div", { className: "space-y-2", children: feedbackList.length === 0 ? (_jsx("div", { className: "flex justify-between items-center p-2 bg-gray-50 rounded text-sm", children: "No Learnings found" })) : (feedbackList.map((feedback) => (_jsxs("div", { className: "flex justify-between items-center p-2 bg-gray-50 rounded text-sm", children: [_jsx("span", { children: feedback.suggestedReplacement }), _jsx("i", { className: "pi pi-pencil cursor-pointer", onClick: () => handleEdit(feedback) })] }, feedback.id)))) }) }) }))] }), _jsxs("div", { className: "", children: [_jsx("h3", { className: "mb-2 font-medium", children: "Apply to" }), _jsx(Dropdown, { disabled: true, value: formData.applyTo, options: applyToOptions, onChange: (e) => handleInputChange("applyTo", e.value), className: "w-full border border-[#D1D5DB] rounded-[6px] text-sm", panelClassName: "shadow-md border border-[#d1d5db] rounded-[8px] mt-1 text-sm" })] }), _jsxs("div", { className: "", children: [_jsx("h3", { className: "mb-2 font-medium", children: "When to Apply" }), _jsx(InputText, { value: formData.whenToApply, onChange: (e) => handleInputChange("whenToApply", e.target.value), className: "w-full p-3 text-sm rounded-[6px] border border[#d1d5db]" })] }), _jsxs("div", { className: "", children: [_jsx("h3", { className: "mb-2 font-medium", children: "When Not to Apply" }), _jsx(InputText, { value: formData.whenNotToApply, onChange: (e) => handleInputChange("whenNotToApply", e.target.value), className: "w-full p-3 text-sm rounded-[6px] border border[#d1d5db]" })] }), _jsxs("div", { className: "flex justify-end gap-2", children: [_jsxs(Button, { className: "rounded-[6px] border border-[#D1D5DB] text-sm text-[#344054] px-4 py-2 flex flex-row items-center gap-2", onClick: onHide, children: [_jsx(X, { size: 16, color: "#5928ed" }), _jsx("span", { children: "Cancel" })] }), _jsxs(Button, { className: "rounded-[6px] px-4 py-2 text-sm text-white flex items-center gap-x-2 transition-all duration-300 bg-gradient-to-r from-[#2546F0] to-[#5928ED] hover:from-[#0039cc] hover:to-[#0039cc]", onClick: handleSave, children: [_jsx(Check, { size: 16 }), _jsx("span", { children: "Save Changes" })] })] })] }));
    return isEditing && selectedFeedback ? (_jsx(EditFeedback, { data: selectedFeedback, onSave: handleUpdateFeedback, modeName: feedbackData.modeName, onCancel: () => {
            setIsEditing(false);
            setSelectedFeedback(null);
        } })) : (renderCreateForm());
};
